import { useEffect } from "react";
import { FAQS } from "../data/content";

export const SITE_URL = "https://eletech.ca";
export const SITE_TITLE = "Eletech Global Ontario School";
export const SITE_DESC =
  "Eletech Global Ontario School is a ministry-inspected private online high school. Earn OSSD credits anytime, anywhere, with Ontario Certified Teachers.";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

export const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.flatMap((c) =>
    c.items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    }))
  ),
};

export default function Seo({ title, description, path, image = "/og-image.png", noindex = false, jsonld = null }) {
  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    const url = `${SITE_URL}${path}`;
    upsertCanonical(url);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", `${SITE_URL}${image}`);
    upsertMeta("name", "twitter:card", "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", `${SITE_URL}${image}`);
    let el = document.getElementById("route-jsonld");
    if (jsonld) {
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = "route-jsonld";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(jsonld);
    } else if (el && el.dataset.static !== "true") {
      // keep build-time (prerendered) schema; only remove client-injected leftovers
      el.remove();
    }
  }, [title, description, path, image, noindex, jsonld]);
  return null;
}

export const SEO = {
  "/": {
    title: "Earn Your Ontario Secondary School Diploma Online | EGOS",
    description:
      "A ministry-inspected private online high school. Earn Ontario Secondary School Diploma (OSSD) credits online — Ontario Certified Teachers, transcripts sent directly.",
    path: "/",
  },
  "/why-egos": {
    title: "Why EGOS — 10 Reasons Families Choose Our School",
    description:
      "Discover ten reasons families choose EGOS — Ontario Certified Teachers, fast feedback on your work, flexible pacing, and a graduation plan built around you.",
    path: "/why-egos",
  },
  "/courses": {
    title: "OSSD Online Courses — Grade 9 to 12 Credit List | EGOS",
    description:
      "Browse every Grade 9 to 12 OSSD credit — English, maths, sciences, business and more — with tuition per credit, fast-track grading and 12 months of access.",
    path: "/courses",
  },
  "/admissions": {
    title: "Apply for OSSD Credits Online — Admissions | EGOS",
    description:
      "Register for OSSD credits in minutes. Choose your pathway, pick your courses, and see tuition, scholarships and any fees before you submit your application.",
    path: "/admissions",
  },
  "/process": {
    title: "How Admission Works — OSSD Enrolment Steps | EGOS",
    description:
      "From your first enquiry to your first login — how EGOS admission works, what documents you need, and how quickly you can start earning OSSD credits.",
    path: "/process",
  },
  "/tuition": {
    title: "Tuition & Fees — OSSD Pricing and Scholarships | EGOS",
    description:
      "Transparent OSSD tuition: domestic and international per-credit fees, bundled scholarships, what every fee includes, student stories and our refund policy.",
    path: "/tuition",
  },
  "/international": {
    title: "OSSD for International Students — Apply Like a Local",
    description:
      "Earn a ministry-inspected OSSD from home and apply to Canadian universities like a local student — Group A Stream, PLAR credits, and zero relocation costs.",
    path: "/international",
  },
  "/faq": {
    title: "FAQ — Online OSSD Credits, Grading and Fees | EGOS",
    description:
      "Answers about studying online with EGOS — eligibility, credit transfers, grading, exams, tuition, scholarships and refunds for OSSD students and parents.",
    path: "/faq",
    jsonld: FAQ_JSONLD,
  },
  "/blog": {
    title: "Blog — OSSD Guides for Students & Parents | EGOS",
    description:
      "Practical guides on earning your Ontario Secondary School Diploma online — OUAC streams, PLAR credits, grading, university pathways and study planning.",
    path: "/blog",
  },
  "/contact": {
    title: "Contact EGOS — Email, Office Hours & Meeting Booking",
    description:
      "Reach EGOS admissions by email, browse office hours, or book a 15-minute meeting. Our team replies within one business day.",
    path: "/contact",
  },
  "/demo": {
    title: "Live Demo — Try the EGOS Learning Platform",
    description:
      "Explore a live demo of the EGOS learning platform — browse courses, see the student centre and experience online learning before you enrol.",
    path: "/demo",
  },
  "/privacy": {
    title: "Privacy Policy | Eletech Global Ontario School",
    description:
      "How Eletech Global Ontario School collects, uses and protects student and family information.",
    path: "/privacy",
  },
  "/refund-policy": {
    title: "Refund Policy | Eletech Global Ontario School",
    description:
      "EGOS refund policy — tuition refund windows and terms, and which administrative fees are non-refundable.",
    path: "/refund-policy",
  },
  "/checkout": {
    title: "Checkout | EGOS",
    description: "Complete your EGOS administrative fee order.",
    path: "/checkout",
    noindex: true,
  },
};
